import { TrainingSetting } from "./../shared/types/trainingSetting.type";
import axios, { AxiosError, AxiosResponse } from "axios";
import { loginForm } from "../components/auth/LoginForm";
import { TUser } from "../shared/types/user.type";
import axiosInstance from "./base";
import { TFile } from "../shared/types/file.type";
import { TQualifyChoices } from "../shared/types/qualifyChoices.type";
import { TThirdPartyApp } from "../shared/types/thirdParty.type";
import { TPlatformConfiguration } from "../shared/types/platformConfiguration";
import EquimetreRequestConfig from "./equimetreRequestConfig";
import { TKeyParamLayout } from "../shared/types/exam.type";
import { TParentChild } from "../shared/types/parentChild.type";
import { CreateParentChildDto } from "../dto/CreateParentChildDto";
import { UserActivityLogPayload } from "../hooks/useUserActivityLog";
import { IAdminMsgView } from "../dto/CreateAdminMessageViewDto";
import { CreateEcgInvoiceDto } from "../dto/CreateEcgInvoiceDto";
import { Fleet } from "../shared/types/fleet.type";
import { PatchFleetDto } from "../dto/patchFleetDto";
import { Reason } from "../shared/types/reasons.type";

export class User {
  static register(data: loginForm): Promise<AxiosResponse<TUser>> {
    return axiosInstance.post("users", data);
  }

  static login(data: loginForm): Promise<
    AxiosResponse<{
      token: string;
      refresh_token: string;
      user_id: number;
      first_web_login?: boolean | never;
    }>
  > {
    return axiosInstance.post("authentication-token", data);
  }
  static update(data: Partial<TUser>): Promise<AxiosResponse<TUser>> {
    return axiosInstance.patch(`users/${data.id}`, data, {
      headers: {
        "Content-Type": "application/merge-patch+json",
      },
    });
  }

  static resetPassword(data: { password: string; token: string }) {
    return axiosInstance.post("reset-password-submit ", data);
  }

  static requestResetPassword(data: { email: string }) {
    return axiosInstance.post("reset-password-request ", data);
  }

  static uploadImage(data: FormData): Promise<AxiosResponse<TFile>> {
    return axiosInstance.post("upload_file", data);
  }

  static uploadLargeFile(data: FormData): Promise<AxiosResponse<TFile>> {
    return axiosInstance.post("upload_big_file", data);
  }

  static getUser(queryParams?: string): Promise<AxiosResponse<TUser>> {
    return axiosInstance.get("users/me".concat(queryParams ?? ""));
  }

  static getConfigurations(
    userId: string,
  ): Promise<AxiosResponse<TPlatformConfiguration[]>> {
    return axiosInstance.get(`configurations?user=${userId}`, {
      useHydraArrayTransform: true,
    } as EquimetreRequestConfig);
  }

  static getJobs() {
    return axiosInstance.get("jobs");
  }

  static updateSettings(
    id: number,
    data: Partial<TrainingSetting>,
  ): Promise<AxiosResponse<TrainingSetting>> {
    return axiosInstance.patch(`user_training_settings/${id}`, data, {
      headers: {
        "Content-Type": "application/merge-patch+json",
      },
    });
  }

  static getQualifyChoices(
    id?: number,
    lang: string = "en",
  ): Promise<AxiosResponse<TQualifyChoices>> {
    return axiosInstance.get(
      `users/${id}/qualify_training_choices?lang=${lang}`,
    );
  }

  static updateThirdParty(
    id: number,
    data: Partial<TThirdPartyApp>,
  ): Promise<AxiosResponse<TThirdPartyApp>> {
    return axiosInstance.patch(`third_party_api_access_requests/${id}`, data, {
      headers: {
        "Content-Type": "application/merge-patch+json",
      },
    });
  }

  static getParamsLayout(
    trainer?: string,
    lang = "en",
  ): Promise<AxiosResponse<TKeyParamLayout[]>> {
    return axiosInstance.get(
      `key_parameters_layouts?trainer=${trainer}&lang=${lang}`,
    );
  }

  static getThirdPartyUsers(
    userId?: string,
    options?: string,
  ): Promise<AxiosResponse<TParentChild[]>> {
    return axiosInstance.get(
      `parent_child_horses?child=${userId}`.concat(
        options ? `&${options}` : "",
      ),
    );
  }
  static getThirdPartyUsersChilds(
    userId?: string,
    options?: string,
  ): Promise<AxiosResponse<TParentChild[]>> {
    return axiosInstance.get(
      `parent_child_horses?parent=${userId}`.concat(
        options ? `&${options}` : "",
      ),
    );
  }

  static deleteParentChild(id: number) {
    return axiosInstance.delete(`parent_child_horses/${id}`);
  }

  static getUsers(params?: string[]): Promise<AxiosResponse<TUser[]>> {
    return axiosInstance.get(
      "users".concat(params ? `?${params.join("&")}` : ""),
    );
  }

  static saveParentChild(data: CreateParentChildDto) {
    return axiosInstance.post("parent_child_horses", data);
  }

  static refreshUserToken(refresh_token: string) {
    return axiosInstance.post("token/refresh", {
      refresh_token,
    });
  }

  static getArioneoInstitue(
    user?: number,
  ): Promise<AxiosResponse<{ url: string }>> {
    return axiosInstance.get(`users/${user}/arioneo_institute_promo_link`);
  }

  static searchByEmail(email: string): Promise<AxiosResponse<TUser | null>> {
    return axiosInstance.get(`users/email/${email}`);
  }

  static sendTrackingLogs(
    data: UserActivityLogPayload,
  ): Promise<AxiosResponse<any>> {
    return axiosInstance.post(`user_activity_logs`, data);
  }

  static removeUserMessage(data: IAdminMsgView): Promise<AxiosResponse<any>> {
    return axiosInstance.post(`user_admin_message_views`, data);
  }

  static createEcgInvoice(
    data: CreateEcgInvoiceDto,
  ): Promise<AxiosResponse<any>> {
    return axios.post(
      (process.env.REACT_APP_QUOTE_API_BASE_URI ?? "").concat(
        `createECGInvoice`,
      ),
      data,
    );
  }
  static getFleet(
    id?: number,
    options?: string[],
  ): Promise<AxiosResponse<Fleet[]>> {
    return axiosInstance.get(
      `users/${id}/fleet?`.concat(options ? options.join("&") : ""),
    );
  }

  static getReasons(): Promise<AxiosResponse<Reason[]>> {
    return axiosInstance.get(`consequence_codes?proposed_to_client=true`);
  }

static patchFleetName(id: number, data: PatchFleetDto) {
  console.log("service", JSON.stringify(data)); // first line
  return axiosInstance.patch(
    `arioneo_device_assignations/${id}`,
    JSON.stringify(data),
    {
      headers: { "Content-Type": "application/merge-patch+json" },
      transformRequest: [(body) => body],
    },
  );
}
}
